import dayjs from "dayjs";
import { applyHotelPortalCancellationDisplayOffset } from "../../helpers/dateHelper";

const penaltyAmountIsZero = (policy) => {
  const value = parseFloat(String(policy?.penaltyAmount ?? policy?.PenaltyAmount ?? "").trim());
  return !Number.isNaN(value) && value === 0;
};

export const isSyntheticHotelCancellationPolicy = (policy) => {
  if (!policy) return true;
  const from = String(policy?.fromDate ?? policy?.FromDate ?? "").trim();
  const to = String(policy?.toDate ?? policy?.ToDate ?? "").trim();
  // Tripjack NR review table uses a real date window — keep for checkout display.
  if (from && to && from !== to) return false;
  const name = String(policy?.policyName ?? policy?.PolicyName ?? "").toLowerCase();
  if (name.includes("non-refund")) return true;
  const penalty = parseFloat(
    String(policy?.penaltyAmount ?? policy?.PenaltyAmount ?? "").trim()
  );
  return (
    !Number.isNaN(penalty) &&
    penalty >= 100 &&
    Boolean(from) &&
    Boolean(to) &&
    from === to
  );
};

/** Match Room Details hover table — show % not fixed INR when supplier sends gross amounts. */
export const formatHotelCancellationPenaltyDisplay = (
  penaltyAmount,
  roomTotal,
  { freeLabel = "0%" } = {}
) => {
  const amount = parseFloat(String(penaltyAmount ?? "").trim());
  if (Number.isNaN(amount)) return String(penaltyAmount ?? "");
  if (amount === 0) return freeLabel;
  if (amount <= 100) return `${amount}%`;

  const total = parseFloat(
    String(
      roomTotal ??
        0
    )
  );
  if (!Number.isNaN(total) && total > 0) {
    const pct = Math.round((amount / total) * 100);
    if (pct >= 100) return "100%";
    if (pct > 0) return `${pct}%`;
  }
  return "100%";
};

/** Checkout NR: keep supplier slabs (INR amounts + date window), not synthetic FC placeholders. */
export const getHotelNonRefundableCheckoutPolicies = (ratePlan) => {
  const policies = Array.isArray(ratePlan?.cancellationPolicy)
    ? [...ratePlan.cancellationPolicy]
    : Array.isArray(ratePlan?.CancellationPolicy)
      ? [...ratePlan.CancellationPolicy]
      : [];
  const withDates = policies.filter((p) => {
    if (!p) return false;
    const from = String(p?.fromDate ?? p?.FromDate ?? "").trim();
    const to = String(p?.toDate ?? p?.ToDate ?? "").trim();
    return Boolean(from || to);
  });
  const list = withDates.length > 0 ? withDates : policies.filter(Boolean);
  list.sort((a, b) => {
    const da = dayjs(a?.fromDate ?? a?.FromDate);
    const db = dayjs(b?.fromDate ?? b?.FromDate);
    if (da.isValid() && db.isValid()) return da.valueOf() - db.valueOf();
    return 0;
  });
  return list;
};

/** NR checkout table — hide only the free-cancel (0%) slab row; keep all charged rows. */
export const getHotelNonRefundableCheckoutTablePolicies = (ratePlan) => {
  const all = getHotelNonRefundableCheckoutPolicies(ratePlan);
  return all.filter((policy) => !penaltyAmountIsZero(policy));
};

/** @deprecated kept for hot-reload bundles; NR rows with 0% are filtered before display. */
export const formatHotelNonRefundablePenaltyDisplay = (
  penaltyAmount,
  roomTotal,
) =>
  formatHotelCancellationPenaltyDisplay(penaltyAmount, roomTotal, {
    freeLabel: "100%",
  });

export const formatHotelApiCancellationPolicyDate = (rawDate) => {
  const d = dayjs(rawDate);
  return d?.isValid?.() && d.isAfter(dayjs("1950-01-01"))
    ? d.format("DD-MM-YYYY")
    : String(rawDate ?? "");
};

export const buildHotelNonRefundableCheckoutCancellationText = (ratePlan) => {
  if (!ratePlan) return "";
  const fromSlabs = buildHotelCancellationNarrativeFromSlabsExcludingFree(ratePlan);
  if (fromSlabs) return fromSlabs;

  const narrative = String(
    ratePlan?.supplierCancellationNarrative ??
      ratePlan?.SupplierCancellationNarrative ??
      "",
  ).trim();
  if (narrative) {
    const filtered = narrative
      .split(/(?<=\.)\s+/)
      .map((line) => line.trim())
      .filter(
        (line) => line.length > 0 && !supplierCancellationLineImpliesFreeCancel(line),
      )
      .join(" ");
    if (filtered) return filtered;
  }

  const policies = getHotelNonRefundableCheckoutTablePolicies(ratePlan);
  if (policies.length === 0) return "";

  const roomTotal = resolveHotelCancellationPenaltyBasisTotal(ratePlan);
  const lines = policies.map((p) => {
    const from = formatHotelApiCancellationPolicyDate(
      p?.fromDate ?? p?.FromDate,
    );
    const to = formatHotelApiCancellationPolicyDate(p?.toDate ?? p?.ToDate);
    const penalty = formatHotelCancellationPenaltyDisplay(
      p?.penaltyAmount ?? p?.PenaltyAmount,
      roomTotal,
      { freeLabel: "100%" },
    );
    if (from && to) {
      return `If cancelled on or after ${from} and on or before ${to}, cancellation charges: ${penalty}.`;
    }
    const dateLabel = from || to;
    if (dateLabel) {
      return `Cancellation charges ${penalty} (${dateLabel}).`;
    }
    return `Cancellation charges: ${penalty}.`;
  });
  return lines.join(" ");
};

/** Tripjack review bullets (service fee, no-show, early checkout, taxes) — NR checkout only. */
export const getHotelNonRefundableCheckoutBulletLines = (ratePlan) => {
  if (!ratePlan) return [];
  const fromPolicyArrays = (ratePlan?.cancellationPolicy ??
    ratePlan?.CancellationPolicy ??
    [])
    .flatMap((p) => (Array.isArray(p?.policies) ? p.policies : []))
    .map((line) => String(line ?? "").trim())
    .filter((t) => t.length > 12);
  if (fromPolicyArrays.length > 0) {
    return fromPolicyArrays.slice(0, 6);
  }
  const narrative = String(
    ratePlan?.supplierCancellationNarrative ??
      ratePlan?.SupplierCancellationNarrative ??
      "",
  ).trim();
  if (!narrative) return [];
  const byNewline = narrative
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 12);
  if (byNewline.length >= 2) return byNewline.slice(0, 6);
  const bySentence = narrative
    .split(/(?<=\.)\s+(?=[A-Z])/)
    .map((s) => s.trim())
    .filter((s) => s.length > 12);
  if (bySentence.length >= 2) return bySentence.slice(0, 6);
  return [narrative.replace(/#!#|#\^#/g, "")];
};

/** NR checkout — supplier copy must not imply free cancel / 0% windows. */
export const supplierCancellationLineImpliesFreeCancel = (line) => {
  const text = String(line ?? "").toLowerCase();
  return (
    /\b0\s*%/.test(text) ||
    /\b0\.0+\s*%/.test(text) ||
    /free cancel|cancel for free|free cancellation/.test(text) ||
    /before 12:00 am[^.]*-\s*0\s*%/.test(text) ||
    /cancellation charges:\s*free\b/.test(text) ||
    /charges:\s*free\b/.test(text)
  );
};

/** Same slab narrative as FC checkout, minus lines that show 0% / Free. */
export const buildHotelCancellationNarrativeFromSlabsExcludingFree = (
  ratePlan,
) => {
  const full = buildHotelCancellationNarrativeFromSlabs(ratePlan);
  if (!full) return "";

  const hasSuffix = /this policy is in local time of the property\.?/i.test(
    full,
  );
  const body = full
    .replace(/\s*This policy is in local time of the property\.\s*$/i, "")
    .trim();
  const lines = body
    .split(/(?<=\.)\s+/)
    .map((line) => line.trim())
    .filter(
      (line) => line.length > 0 && !supplierCancellationLineImpliesFreeCancel(line),
    );
  if (lines.length === 0) return "";
  const joined = lines.join(" ");
  return hasSuffix
    ? `${joined} This policy is in local time of the property.`
    : joined;
};

/** Grey policy box under Non-refundable — hide only 0% lines; keep rest of supplier copy. */
export const getHotelNonRefundableCheckoutDisplayLines = (ratePlan) => {
  if (!ratePlan) return ["Non-refundable"];

  const fromSlabs = buildHotelCancellationNarrativeFromSlabsExcludingFree(ratePlan);
  if (fromSlabs) {
    const hasSuffix = /local time of the property/i.test(fromSlabs);
    const body = fromSlabs
      .replace(/\s*This policy is in local time of the property\.\s*$/i, "")
      .trim();
    const lines = body
      .split(/(?<=\.)\s+/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    if (lines.length > 0) {
      return hasSuffix
        ? [...lines, "This policy is in local time of the property."]
        : lines;
    }
  }

  const safeBullets = getHotelNonRefundableCheckoutBulletLines(ratePlan).filter(
    (line) => !supplierCancellationLineImpliesFreeCancel(line),
  );
  if (safeBullets.length > 0) {
    return safeBullets;
  }

  const fallback = buildHotelNonRefundableCheckoutCancellationText(ratePlan);
  if (fallback) {
    return fallback
      .split(/(?<=\.)\s+/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  return ["Non-refundable"];
};

export const buildHotelNonRefundableCheckoutCancellationSummary = (ratePlan) => {
  const bullets = getHotelNonRefundableCheckoutBulletLines(ratePlan);
  if (bullets.length > 0) return bullets.join(" ");
  return buildHotelNonRefundableCheckoutCancellationText(ratePlan);
};

export const resolveHotelRatePlanRoomTotal = (ratePlan) => {
  if (!ratePlan?.price) return 0;
  const candidates = [
    ratePlan.price.total,
    ratePlan.price.offerPrice,
    ratePlan.price.supplierNet,
    ratePlan.price.base,
  ];
  for (const value of candidates) {
    const n = parseFloat(value);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return 0;
};

/** Supplier fare for cancellation % — same on guest checkout (+markup) and agent login. */
export const resolveHotelCancellationPenaltyBasisTotal = (ratePlan) => {
  if (!ratePlan?.price) return 0;
  const price = ratePlan.price;

  for (const value of [
    price.tripjackSupplierGross,
    price.supplierGross,
    price.SupplierGross,
  ]) {
    const n = parseFloat(value);
    if (!Number.isNaN(n) && n > 0) return n;
  }

  const markupRate = parseFloat(price.portalMarkupRate ?? 0);
  const displayTotal = parseFloat(price.total ?? price.Total ?? 0);
  if (markupRate > 0 && displayTotal > 0) {
    const unraveled = displayTotal / (1 + markupRate);
    if (!Number.isNaN(unraveled) && unraveled > 0) {
      return Math.ceil(unraveled);
    }
  }

  const discountRate = parseFloat(price.portalDiscountRate ?? 0);
  if (discountRate > 0 && discountRate < 1 && displayTotal > 0) {
    const unraveled = displayTotal / (1 - discountRate);
    if (!Number.isNaN(unraveled) && unraveled > 0) {
      return Math.ceil(unraveled);
    }
  }

  return resolveHotelRatePlanRoomTotal(ratePlan);
};

export const formatHotelCancellationDisplayDateLong = (rawDate) => {
  const shifted = applyHotelPortalCancellationDisplayOffset(rawDate);
  return shifted?.isValid?.() && shifted.isAfter(dayjs("1950-01-01"))
    ? shifted.format("DD MMMM")
    : "";
};

export const buildHotelCancellationNarrativeFromSlabs = (ratePlan) => {
  if (!ratePlan) return "";

  const policies = Array.isArray(ratePlan?.cancellationPolicy)
    ? [...ratePlan.cancellationPolicy].filter(
        (p) => !isSyntheticHotelCancellationPolicy(p)
      )
    : [];
  if (policies.length === 0) return "";

  policies.sort((a, b) => {
    const da = dayjs(a?.fromDate ?? a?.toDate);
    const db = dayjs(b?.fromDate ?? b?.toDate);
    if (da.isValid() && db.isValid()) return da.valueOf() - db.valueOf();
    return 0;
  });

  const roomTotal = resolveHotelCancellationPenaltyBasisTotal(ratePlan);
  const lines = [];

  policies.forEach((p, index) => {
    const penalty = parseFloat(p?.penaltyAmount);
    const toRaw = p?.toDate || p?.fromDate;
    const fromRaw = p?.fromDate || p?.toDate;
    const toParsed = toRaw ? dayjs(toRaw) : null;
    const fromParsed = fromRaw ? dayjs(fromRaw) : null;
    const toLabel =
      toParsed?.isValid?.() && toParsed.isAfter(dayjs("1950-01-01"))
        ? toParsed.format("DD MMMM")
        : "";
    const fromLabel =
      fromParsed?.isValid?.() && fromParsed.isAfter(dayjs("1950-01-01"))
        ? fromParsed.format("DD MMMM")
        : "";

    if (!Number.isNaN(penalty) && penalty === 0) {
      const freeLabel = formatHotelCancellationDisplayDateLong(toRaw || fromRaw);
      if (freeLabel) {
        lines.push(
          `Charges for cancellations done before 12:00 AM, ${freeLabel} - ${formatHotelCancellationPenaltyDisplay(0, roomTotal)}.`
        );
      }
      return;
    }

    if (!Number.isNaN(penalty) && penalty > 0) {
      const rawForLabel =
        index === 0 && (toRaw || fromRaw) ? toRaw || fromRaw : fromRaw || toRaw;
      const label = formatHotelCancellationDisplayDateLong(rawForLabel);
      if (label) {
        lines.push(
          `Charges for cancellations done after 12:00 AM, ${label} - ${formatHotelCancellationPenaltyDisplay(penalty, roomTotal)}.`
        );
      }
    }
  });

  const policyLines = policies.flatMap((p) =>
    Array.isArray(p?.policies)
      ? p.policies.map((line) => String(line ?? "").trim()).filter((t) => t.length > 20)
      : []
  );
  if (lines.length === 0 && policyLines.length > 0) {
    return policyLines[0];
  }
  if (lines.length === 0) return "";
  return `${lines.join(" ")} This policy is in local time of the property.`;
};

/** When FC selection has a deadline but slabs/scnp are still missing (pre-enrich). */
export const buildHotelFreeCancelFallbackNarrative = (ratePlan) => {
  if (!ratePlan || ratePlan.refundable === false || ratePlan.Refundable === false) {
    return "";
  }
  const raw =
    resolveHotelApiCancellationDeadline(ratePlan) ||
    getHotelRatePlanFreeCancelDate(ratePlan) ||
    ratePlan?.lastCancellationDate ||
    ratePlan?.LastCancellationDate ||
    ratePlan?.lastVoucherDate ||
    ratePlan?.LastVoucherDate ||
    "";
  const label = formatHotelCancellationDisplayDateLong(raw);
  if (!label) return "";
  return `Charges for cancellations done before 12:00 AM, ${label} - 0%. This policy is in local time of the property.`;
};

export const resolveHotelCancellationNarrative = (ratePlan) => {
  const fromSlabs = buildHotelCancellationNarrativeFromSlabs(ratePlan);
  if (fromSlabs) return fromSlabs;
  const scnp = String(
    ratePlan?.supplierCancellationNarrative ??
      ratePlan?.SupplierCancellationNarrative ??
      ""
  ).trim();
  if (scnp && !/^non[\s-]*refundable\.?$/i.test(scnp)) {
    return scnp;
  }
  if (
    ratePlan?.refundable === true ||
    ratePlan?.Refundable === true ||
    ratePlan?.tripjackIfra === true
  ) {
    return buildHotelFreeCancelFallbackNarrative(ratePlan);
  }
  return scnp;
};

export const normalizeHotelRatePlanRefundableFields = (ratePlan = {}) => {
  if (!ratePlan || typeof ratePlan !== "object") return ratePlan;
  if (ratePlan.Refundable === true || ratePlan.refundable === true) {
    ratePlan.refundable = true;
    ratePlan.Refundable = true;
  }
  if (ratePlan.tripjackIfra === true || ratePlan.TripjackIfra === true) {
    ratePlan.tripjackIfra = true;
  }
  const narrative = String(
    ratePlan.supplierCancellationNarrative ??
      ratePlan.SupplierCancellationNarrative ??
      ""
  ).trim();
  if (narrative) {
    ratePlan.supplierCancellationNarrative = narrative;
  }
  return ratePlan;
};

export const isHotelRatePlanRefundable = (ratePlan) => {
  if (!ratePlan) return false;
  // Explicit NR from Details/checkout selection must win over narrative/slabs.
  if (ratePlan.refundable === false || ratePlan.Refundable === false) {
    return false;
  }
  normalizeHotelRatePlanRefundableFields(ratePlan);
  if (ratePlan.refundable === true || ratePlan.tripjackIfra === true) {
    return true;
  }

  const narrative = String(ratePlan.supplierCancellationNarrative ?? "").trim();
  if (
    narrative &&
    (/free cancellation|cancel for free|fully refundable/i.test(narrative) &&
      !/non[\s-]*refund/i.test(narrative))
  ) {
    return true;
  }
    if (
      narrative &&
      /prior to arrival|days?\s+prior|before arrival|avoid a penalty/i.test(
        narrative,
      ) &&
      /cancel/i.test(narrative) &&
      !/non[\s-]*refund/i.test(narrative)
    ) {
      return true;
    }

  const policies = (ratePlan.cancellationPolicy ?? ratePlan.CancellationPolicy ?? []).filter(
    (policy) => !isSyntheticHotelCancellationPolicy(policy)
  );
  if (policies.some(penaltyAmountIsZero)) {
    return true;
  }

  return false;
};

/** Real cancellation slabs with every penalty > 0 → Tripjack non-refundable. */
export const ratePlanSlabsIndicateNonRefundable = (ratePlan) => {
  const policies = (
    ratePlan?.cancellationPolicy ??
    ratePlan?.CancellationPolicy ??
    []
  ).filter((policy) => !isSyntheticHotelCancellationPolicy(policy));
  if (policies.length === 0) return false;
  return !policies.some(penaltyAmountIsZero);
};

/**
 * Explicit refundable flag wins over tripjackIfra.
 * (hotelprice often leaves tripjackIfra:false on an FC selection → checkout was flipping to NR.)
 * Explicit refundable:false still wins first so NR selections stay NR.
 */
export const isHotelRatePlanNonRefundableByApi = (ratePlan) => {
  if (!ratePlan) return true;
  if (ratePlan.__tripjackListingFcMeta === true) {
    return false;
  }
  if (ratePlan.refundable === false || ratePlan.Refundable === false) {
    return true;
  }
  if (ratePlan.refundable === true || ratePlan.Refundable === true) {
    return false;
  }
  if (ratePlan.tripjackIfra === false || ratePlan.TripjackIfra === false) {
    return true;
  }
  if (ratePlan.tripjackIfra === true || ratePlan.TripjackIfra === true) {
    return false;
  }
  return ratePlanSlabsIndicateNonRefundable(ratePlan);
};

export const isHotelRatePlanRefundableForDisplay = (ratePlan) => {
  if (isHotelRatePlanNonRefundableByApi(ratePlan)) return false;
  return isHotelRatePlanRefundable(ratePlan);
};

/**
 * Checkout/Preview + Hold gate — matches RoomDetailsCard (stricter than API helper alone).
 * Empty slabs / no free-cancel window → Non-refundable, not "Refundable".
 */
export const isHotelRatePlanNonRefundableForCheckout = (ratePlan) => {
  if (!ratePlan) return true;
  if (ratePlan.refundable === false || ratePlan.Refundable === false) {
    return true;
  }
  if (isHotelRatePlanNonRefundableByApi(ratePlan)) {
    return true;
  }
  if (!isHotelRatePlanRefundable(ratePlan)) {
    return true;
  }
  return false;
};

/** True when any selected room rate is non-refundable (Hold must be hidden). */
export const isHotelCheckoutSelectionNonRefundable = (hotelPriceData = {}) => {
  const rooms = Array.isArray(hotelPriceData?.rooms) ? hotelPriceData.rooms : [];
  if (rooms.length === 0) return false;
  return rooms.some((room) => {
    const rp = room?.ratePlans?.[0] ?? room?.RatePlans?.[0];
    return isHotelRatePlanNonRefundableForCheckout(rp);
  });
};

export const getHotelRatePlanFreeCancelDateForDisplay = (ratePlan) => {
  if (isHotelRatePlanNonRefundableByApi(ratePlan)) return null;
  const stamped =
    ratePlan?.lastCancellationDate ??
    ratePlan?.LastCancellationDate ??
    ratePlan?.tripjackCancellationCutoff ??
    ratePlan?.TripjackCancellationCutoff;
  if (stamped) return stamped;
  const policies = (
    ratePlan?.cancellationPolicy ??
    ratePlan?.CancellationPolicy ??
    []
  ).filter((policy) => !isSyntheticHotelCancellationPolicy(policy));
  const freeSlab = policies.find(penaltyAmountIsZero);
  if (freeSlab) {
    return (
      freeSlab.toDate ??
      freeSlab.ToDate ??
      freeSlab.fromDate ??
      freeSlab.FromDate ??
      null
    );
  }
  if (!isHotelRatePlanRefundable(ratePlan)) return null;
  return getHotelRatePlanFreeCancelDate(ratePlan);
};

export const getHotelRatePlanFreeCancelDate = (ratePlan) => {
  if (!ratePlan) return null;
  const policies = (
    ratePlan.cancellationPolicy ??
    ratePlan.CancellationPolicy ??
    []
  ).filter((policy) => !isSyntheticHotelCancellationPolicy(policy));
  return (
    ratePlan.tripjackCancellationCutoff ||
    ratePlan.TripjackCancellationCutoff ||
    ratePlan.lastCancellationDate ||
    ratePlan.LastCancellationDate ||
    ratePlan.lastVoucherDate ||
    ratePlan.LastVoucherDate ||
    policies.find(penaltyAmountIsZero)?.toDate ||
    policies.find(penaltyAmountIsZero)?.ToDate ||
    null
  );
};

/** Calendar YYYY-MM-DD written on the penalty. A trailing Z must not move 23:59 into the next day. */
export const toHotelApiCancellationCalendarYmd = (raw) => {
  if (raw == null || raw === "") return "";
  const text = String(raw).trim();
  const datePart = text.split(/[T\s]/)[0];
  const iso = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const dmy = datePart.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, "0")}-${String(dmy[1]).padStart(2, "0")}`;
  }
  const d = dayjs(raw);
  return d?.isValid?.() ? d.format("YYYY-MM-DD") : "";
};

/**
 * Tripjack free-cancel deadline from review policy slabs (matches Hotels backend).
 * Portal display subtracts HOTEL_PORTAL_CANCELLATION_DISPLAY_OFFSET_DAYS (1).
 */
export const resolveHotelApiCancellationDeadline = (ratePlan) => {
  if (!ratePlan) return "";
  if (isHotelRatePlanNonRefundableForCheckout(ratePlan)) {
    return "";
  }
  const policies = (
    ratePlan.cancellationPolicy ??
    ratePlan.CancellationPolicy ??
    []
  ).filter((policy) => policy && !isSyntheticHotelCancellationPolicy(policy));
  const stamped = toHotelApiCancellationCalendarYmd(
    ratePlan.lastCancellationDate ||
      ratePlan.LastCancellationDate ||
      ratePlan.tripjackCancellationCutoff ||
      ratePlan.TripjackCancellationCutoff ||
      "",
  );
  if (stamped) return stamped;
  const ymds = (values) =>
    values
      .map((value) => toHotelApiCancellationCalendarYmd(value))
      .filter(Boolean)
      .sort();
  const chargeFrom = ymds(
    policies
      .filter((policy) => !penaltyAmountIsZero(policy))
      .map((policy) => String(policy.fromDate ?? policy.FromDate ?? "")),
  )[0];
  if (chargeFrom) {
    return chargeFrom;
  }
  const freeTo = ymds(
    policies
      .filter(
        (policy) =>
          penaltyAmountIsZero(policy) &&
          (policy?.toDate ?? policy?.ToDate),
      )
      .map((policy) => String(policy.toDate ?? policy.ToDate ?? "")),
  ).pop();
  if (freeTo) {
    return freeTo;
  }

  return toHotelApiCancellationCalendarYmd(
    ratePlan.tripjackCancellationCutoff ||
      ratePlan.TripjackCancellationCutoff ||
      ratePlan.lastCancellationDate ||
      ratePlan.LastCancellationDate ||
      ratePlan.lastVoucherDate ||
      ratePlan.LastVoucherDate ||
      "",
  );
};

/** Earliest API deadline across booked rooms (most restrictive). */
/** Align checkout header, room card, and policy hover (FC vs NR). */
export const finalizeCheckoutHotelCancellationDisplay = (hotels = {}) => {
  if (!hotels || typeof hotels !== "object") return hotels;
  const roomsKey = Array.isArray(hotels.rooms)
    ? "rooms"
    : Array.isArray(hotels.Rooms)
      ? "Rooms"
      : null;
  if (!roomsKey) return hotels;

  const nextRooms = hotels[roomsKey].map((room) => {
    const nextPlans = (room?.ratePlans ?? room?.RatePlans ?? []).map((rp) => {
      if (!rp) return rp;
      if (isHotelRatePlanNonRefundableForCheckout(rp)) {
        const policies = getHotelNonRefundableCheckoutTablePolicies(rp);
        const narrative = String(
          rp?.supplierCancellationNarrative ??
            rp?.SupplierCancellationNarrative ??
            "",
        ).trim();
        return {
          ...rp,
          refundable: false,
          Refundable: false,
          tripjackIfra: false,
          TripjackIfra: false,
          lastCancellationDate: undefined,
          LastCancellationDate: undefined,
          lastVoucherDate: undefined,
          LastVoucherDate: undefined,
          tripjackCancellationCutoff: undefined,
          TripjackCancellationCutoff: undefined,
          ...(policies.length > 0 ? { cancellationPolicy: policies } : {}),
          ...(narrative ? { supplierCancellationNarrative: narrative } : {}),
        };
      }
      const deadline = resolveHotelApiCancellationDeadline(rp);
      const freeDate = getHotelRatePlanFreeCancelDateForDisplay(rp);
      const isoDeadline = deadline ? `${deadline}T12:00:00.000Z` : null;
      const policies = (rp?.cancellationPolicy ?? rp?.CancellationPolicy ?? []).filter(
        (policy) => policy && !isSyntheticHotelCancellationPolicy(policy),
      );
      return {
        ...rp,
        refundable: true,
        Refundable: true,
        ...(isoDeadline
          ? {
              tripjackCancellationCutoff: deadline,
              lastCancellationDate: isoDeadline,
              lastVoucherDate: isoDeadline,
            }
          : freeDate
            ? {
                lastCancellationDate: freeDate,
                lastVoucherDate: freeDate,
              }
            : {}),
        ...(policies.length > 0 ? { cancellationPolicy: policies } : {}),
      };
    });
    return { ...room, ratePlans: nextPlans };
  });

  return { ...hotels, [roomsKey]: nextRooms };
};

export const resolveHotelBookingCancellationDeadlineFromRooms = (rooms = []) => {
  if (!Array.isArray(rooms) || rooms.length === 0) {
    return "";
  }
  const deadlines = rooms
    .map((room) => {
      const ratePlan = room?.ratePlans?.[0] ?? room?.RatePlans?.[0];
      return resolveHotelApiCancellationDeadline(ratePlan);
    })
    .filter(Boolean)
    .sort();
  return deadlines[0] ?? "";
};

export const resolveHotelBookingCancellationIsoFromRooms = (rooms = []) => {
  const ymd = resolveHotelBookingCancellationDeadlineFromRooms(rooms);
  return ymd ? `${ymd}T12:00:00.000Z` : "";
};
